import { Typography } from '@material-ui/core';
import { ErrorAPI } from 'api/Assessor/methods';
import VotoPresencialAPI from 'api/Assessor/votoPresencial';
import { AxiosError } from 'axios';
import {
  AssembleiaIcon,
  BarChartIcon,
  CircleCheck,
  SyncIcon,
} from 'components/Icons';
import LayoutContext from 'components/Layout/LayoutContext';
import RespostaPautaAGO, {
  RespostaPautaAGOGenericModalProps,
} from 'components/Modal/__LEGACY__/RespostaPautaAGO';
import RespostaPautaModal, {
  IRespostaPautaModalProps,
} from 'components/Modal/__LEGACY__/RespostaPautaModal/RespostaPautaModal';
import { ApiErrors } from 'helpers/enums/ApiErrors';
import {
  AssembleiaPautaStatus,
  AssembleiaPautaStatusString,
  TipoVotoPauta,
} from 'helpers/enums/AssembleiaPautaStatus';
import { AssembleiaStatus } from 'helpers/enums/AssembleiaStatus';
import { AssembleiaTipo } from 'helpers/enums/AssembleiaTipo';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  assembleiaAssociadoActionsRefresh,
  assembleiasAssociadoActionsRegistraVoto,
} from 'store/modules/AssembleiasAssociadoActions';
import { ASSEMBLEIAS_ASSOCIADO_ACTIONS_REGISTRA_VOTO } from 'store/modules/AssembleiasAssociadoActionTypes';
import {
  IAssembleiaAssociadoPauta,
  IAssembleiaAssociadoPautaVoto,
} from 'store/modules/AssembleiasAssociadoReducer';
import {
  modalsActionsClose,
  modalsActionsOpen,
} from 'store/modules/ModalsActions';
import { IReducer } from 'store/Reducer';
import { assembleiaUtilsUsuariosOnlineByIdAssembleiaOrCurrent } from 'helpers/utils/AssembleiaUtils';
import AxiosErrorHandler, {
  getAxiosErrorApiCode,
} from 'helpers/utils/AxiosErrorHandler';
import ErrorHandler from 'helpers/utils/ErrorHandler';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import {
  getPautaComNovosVotos,
  pautaUtilsGetVotoByIdPauta,
  pautaUtilsSetVoto,
} from 'helpers/utils/PautaUtils';
import useStyles from './style';
import { Button } from 'plataforma-fundacao-componentes';
import translation from 'locales/translation';

export const PautaVotoInvalido: number = -1;

export interface IAssembleiaPautaAssociadoActionButtons {
  loading: boolean;
  setLoading: Function;
  pauta: IAssembleiaAssociadoPauta;
  voto: number;
  onVotoError: Function;
  onVotoSuccess: (idItemPauta?: number) => void;
  isEfetivacaoVoto: boolean;
  canParticipate: boolean;
  cpfEfetivacaoPresencial?: string;
  updateVotoPresencial?: (
    idPauta: number,
    voto: IAssembleiaAssociadoPautaVoto[]
  ) => void;
  canSeeResults?: boolean;
}

export default function AssembleiaPautaAssociadoActionButtons({
  setLoading,
  pauta,
  voto,
  onVotoError,
  onVotoSuccess,
  isEfetivacaoVoto,
  canParticipate,
  cpfEfetivacaoPresencial,
  updateVotoPresencial,
  canSeeResults,
}: IAssembleiaPautaAssociadoActionButtons) {
  const classes = useStyles();
  const { startLoading, finishLoading } = useContext(LayoutContext);
  const [showModal, setShowModal] = useState<any>();
  const [sending, setSending] = useState<boolean>(false);
  const dispatchAction = useDispatch();
  const currentEvent = useSelector(
    (state: IReducer) => state.assembleias.currentEvent
  );
  const usuario = useSelector((state: IReducer) => state.auth.usuario);

  const votoSessao = useCallback(() => {
    const sessionVoto = pautaUtilsGetVotoByIdPauta(
      usuario!.cpfCnpj,
      pauta.idPauta
    );
    if (sessionVoto) {
      return sessionVoto.idItemPauta;
    }
  }, [usuario, pauta]);

  const getVotoAtual = useCallback(() => {
    if (votoSessao() !== undefined) return votoSessao();
    if (pauta.votos && pauta.votos.length) {
      return pauta.votos[0].idItemPauta;
    }
    return PautaVotoInvalido;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pauta.votos]);

  const isAGO = useMemo(() => {
    if (!currentEvent) return false;
    return (
      currentEvent.tipo !== AssembleiaTipo.Nucleo &&
      currentEvent.tipo !== AssembleiaTipo.Reuniao
    );
  }, [currentEvent]);

  const canVote = useMemo(() => {
    if (
      ![
        TipoVotoPauta.Vota,
        TipoVotoPauta.VotaNaoColaborador,
        TipoVotoPauta.VotaNaoRestrito,
      ].includes(pauta.tipoVotoPauta)
    ) {
      return false;
    }
    if (isEfetivacaoVoto) return true;
    if (canParticipate !== undefined && !canParticipate) return false;
    if (usuario?.perfil.associadoRoute.assembleias.pautas.participar)
      return true;
    return false;
  }, [usuario, canParticipate, pauta, isEfetivacaoVoto]);

  const isDisabled = useMemo(() => {
    if (!canVote) return true;
    if (sending) return true;
    if (!voto) return true;
    if (voto === PautaVotoInvalido) return true;
    if (pauta.status !== AssembleiaPautaStatus.Liberada) return true;
    if (voto === getVotoAtual()) return true;
    return false;
  }, [sending, voto, pauta.status, getVotoAtual, canVote]);

  const showError = useCallback(
    (error: AxiosError<ErrorAPI>) => {
      setSending(false);
      AxiosErrorHandler(error);
      onVotoError();
      if (
        getAxiosErrorApiCode(error) === ApiErrors.PautaNaoLiberadaParaVotacao &&
        currentEvent
      ) {
        dispatchAction(
          assembleiaAssociadoActionsRefresh({
            id: currentEvent.id,
            statusChat: currentEvent.statusChat,
            pautas: [
              {
                idPauta: pauta.idPauta,
                status: AssembleiaPautaStatus.Encerrada,
                statusPresencial: AssembleiaPautaStatusString.Encerrada,
                tempoVotacao: 0,
                tempoRestante: 0,
                resultadoLiberado: pauta.resultadoLiberado,
                resultadoLiberadoAssociado: pauta.resultadoLiberadoAssociado,
              },
            ],
            usuariosLogados:
              assembleiaUtilsUsuariosOnlineByIdAssembleiaOrCurrent(
                usuario!.cpfCnpj,
                currentEvent.id,
                currentEvent.usuariosLogados
              ),
            presentes: currentEvent.presentes,
            status: currentEvent.status,
            needLoadQuorum: null,
            data: currentEvent.dataEvento,
            titulo: currentEvent.nome,
            urlTransmissao: currentEvent.urlTransmissao,
          })
        );
      }
    },
    [onVotoError, setSending, dispatchAction, currentEvent, pauta, usuario]
  );

  const registrarVoto = useCallback(() => {
    setSending(true);
    dispatchAction(
      assembleiasAssociadoActionsRegistraVoto(
        voto,
        pauta.idPauta,
        (data) => {
          //VERIFICAR SE ISSO AQUI FUNCIONA
          pautaUtilsSetVoto(usuario!.cpfCnpj, voto, pauta.idPauta);
          //INICIO
          if (currentEvent && currentEvent.pautas) {
            const indexPauta = currentEvent.pautas.findIndex(
              (p) => p.idPauta === pauta.idPauta
            );
            if (indexPauta > -1) {
              const pautaComNovosVotos = getPautaComNovosVotos(
                currentEvent.pautas[indexPauta],
                currentEvent.contasDisponiveis,
                data,
                currentEvent.id
              );
              const newPautas = [...currentEvent.pautas] || [];
              newPautas[indexPauta] = pautaComNovosVotos;
              dispatchAction({
                type: ASSEMBLEIAS_ASSOCIADO_ACTIONS_REGISTRA_VOTO,
                payload: newPautas,
              });
            }
          }
          //FIM
          setSending(false);
          onVotoSuccess(voto);
        },
        (error) => {
          showError(error);
        }
      )
    );
  }, [
    dispatchAction,
    voto,
    pauta.idPauta,
    usuario,
    onVotoSuccess,
    showError,
    currentEvent,
  ]);

  const registrarVotoPresencial = useCallback(async () => {
    setSending(true);
    startLoading('Aguarde, voto sendo processado.');
    const { fail, data } = await VotoPresencialAPI.inserirVotosCPF(
      cpfEfetivacaoPresencial || '',
      voto
    );
    if (fail) {
      finishLoading();
      ErrorHandler(fail);
      setSending(false);
    }
    if (data) {
      updateVotoPresencial && updateVotoPresencial(pauta.idPauta, data);
      finishLoading();
      // toast.success(translation.AssembleiaPauta.acoes.confirmado);
      setSending(false);
    }
  }, [
    voto,
    cpfEfetivacaoPresencial,
    updateVotoPresencial,
    pauta,
    startLoading,
    finishLoading,
  ]);

  const closeModal = () => {
    dispatchAction(modalsActionsClose(showModal));
  };

  const openRespostaPauta = useCallback(() => {
    const modalKey: string = getUniqueKey();
    setShowModal(modalKey);
    dispatchAction(
      modalsActionsOpen(
        RespostaPautaModal,
        {
          genericProps: {
            key: modalKey,
            idPauta: pauta.idPauta,
            isParcial: currentEvent?.status === AssembleiaStatus.Live,
            tipoPauta: pauta.tipoPauta,
          } as IRespostaPautaModalProps,
        },
        modalKey
      )
    );
  }, [dispatchAction, pauta, currentEvent]);

  const openRespostaPautaAGO = useCallback(() => {
    const modalKey: string = getUniqueKey();
    setShowModal(modalKey);
    dispatchAction(
      modalsActionsOpen(
        RespostaPautaAGO,
        {
          genericProps: {
            key: modalKey,
            idPauta: pauta.idPauta,
            isParcial: currentEvent?.status === AssembleiaStatus.Live,
            tipoAssembleia: currentEvent?.tipo,
          } as RespostaPautaAGOGenericModalProps,
        },
        modalKey
      )
    );
  }, [dispatchAction, pauta, currentEvent]);

  useEffect(() => {
    setLoading(sending);
  }, [setLoading, sending]);

  useEffect(() => {
    if (!pauta.resultadoLiberadoAssociado) closeModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pauta.resultadoLiberadoAssociado]);

  const presencaMarcada = useCallback(() => {
    const myUser: any = currentEvent?.contasDisponiveis
      ?.filter((conta) => {
        return conta.cpfCnpj === usuario?.cpfCnpj;
      })
      .map((conta) => {
        return conta.presente;
      });

    return currentEvent?.contasDisponiveis?.length === 1 && myUser[0];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEvent]);

  return (
    <React.Fragment>
      {canVote &&
        currentEvent?.status !== AssembleiaStatus.Encerrado &&
        pauta.status === AssembleiaPautaStatus.Liberada &&
        isEfetivacaoVoto &&
        !pauta.estaVotadoPorOutroRepresentante && (
          <Button
            className={classes.botaoAcoes}
            disabled={isDisabled}
            onClick={registrarVotoPresencial}
            rightIcon={
              sending ? (
                <SyncIcon className='animation-rotate' />
              ) : (
                <CircleCheck noFill />
              )
            }
          >
            {translation.AssembleiaPauta.acoes.confirmar}
          </Button>
        )}
      {!canVote && pauta.tipoVotoPauta === TipoVotoPauta.Colaborador && (
        <Typography component='p' className={classes.adviceVoto}>
          {translation.AssembleiaPauta.textoVotoColaborador}
        </Typography>
      )}
      {!canVote && pauta.tipoVotoPauta === TipoVotoPauta.Restrito && (
        <Typography component='p' className={classes.adviceVoto}>
          {translation.AssembleiaPauta.textoVotoRestrito}
        </Typography>
      )}
      {!canVote &&
        (currentEvent!?.tipo === AssembleiaTipo.GeralOrdinaria ||
          currentEvent!?.tipo === AssembleiaTipo.GeralExtraordinaria ||
          currentEvent!?.tipo ===
            AssembleiaTipo.GeralOrdinariaExtraordinaria) &&
        pauta.tipoVotoPauta === TipoVotoPauta.SemPresenca && (
          <Typography component='p' className={classes.adviceVoto}>
            Assembleia Geral - Votação disponível apenas aos Coordenadores de
            Núcleo da Cooperativa
          </Typography>
        )}
      {presencaMarcada() &&
        canVote &&
        pauta.status === AssembleiaPautaStatus.Liberada &&
        pauta.estaVotadoPorOutroRepresentante && (
          <Typography component='p' className={classes.adviceVoto}>
            {`Voto do núcleo já foi confirmado por ${pauta.representante}. Em caso de dúvidas, entre em contato com sua
            Cooperativa.`}
          </Typography>
        )}
      {presencaMarcada() &&
        !canVote &&
        isAGO &&
        pauta.status === AssembleiaPautaStatus.Liberada &&
        usuario?.cpfCnpj !== pauta.votos[0]!?.cpfRepresentante! &&
        !currentEvent?.userCanVote && (
          <Typography component='p' className={classes.adviceVoto}>
            {`Nas Assembleias Gerais, a votação é liberada apenas para um dos
            coordenadores de núcleo presente (efetivo ou suplente). As opções de
            voto estão bloqueadas pois ${pauta.representante}
            está participando ou participou da votação, representando seu núcleo.`}
          </Typography>
        )}
      {canVote &&
        currentEvent?.status !== AssembleiaStatus.Encerrado &&
        pauta.status === AssembleiaPautaStatus.Liberada &&
        !isEfetivacaoVoto &&
        !pauta.estaVotadoPorOutroRepresentante && (
          <Button
            className={classes.botaoAcoes}
            disabled={isDisabled}
            onClick={registrarVoto}
            leftIcon={sending ? <SyncIcon animated /> : <AssembleiaIcon />}
          >
            {pauta.votos.length === 0 &&
              `${translation.AssembleiaPauta.acoes.confirmar}`}
            {currentEvent?.bloqAlteraVoto === true &&
              pauta.votos.length > 0 &&
              `${translation.AssembleiaPauta.bottomMessage.votoEnviado}`}
            {currentEvent?.bloqAlteraVoto === false &&
              pauta.votos.length > 0 &&
              `${translation.AssembleiaPauta.bottomMessage.alterarVoto}`}
          </Button>
        )}
      {pauta.resultadoLiberadoAssociado &&
        !isEfetivacaoVoto &&
        canSeeResults && (
          <Button
            className={classes.botaoAcoes}
            onClick={() => {
              isAGO ? openRespostaPautaAGO() : openRespostaPauta();
            }}
            leftIcon={<BarChartIcon />}
          >
            {
              translation.AssembleiaPauta.acoes[
                currentEvent?.status === AssembleiaStatus.Encerrado ||
                pauta.status === AssembleiaPautaStatus.Encerrada
                  ? 'resultadoFinal'
                  : 'resultadoParcial'
              ]
            }
          </Button>
        )}
    </React.Fragment>
  );
}
